import { navigate } from '@reach/router';
import { User, UserManager } from 'oidc-client';
import React, { useContext, useRef, useState } from 'react';
import { ConfigContext } from '../config/ConfigContext';
import { useOnMount } from '../helpers/hooks/useOnMount';
import { AuthenticationContext, AuthenticationContextValue } from './AuthenticationContext';
import { loggedOutUrl, logInRedirectUrl } from './authenticationUrls';
import { getUserManagerSettings } from './userManagerSettings';

const postLogInRedirectUrlStorageKey = 'quotation-and-design:post-log-in-redirect-url';

type State = {
  user: User | null;
  authenticationError: Error | null;
  isLoggingIn: boolean;
  isLoggingOut: boolean;
};

export type AuthenticationProps = {
  isAuthenticated: boolean;
  authenticationError: Error | null;
  isLoggingIn: boolean;
  isLoggingOut: boolean;
  redirectToLogin: () => void;
  logOut: () => void;
};

type OwnProps = {
  children: (authentication: AuthenticationProps) => React.ReactNode;
};

const isLogInRedirectCallback = () => window.location.pathname === `/${logInRedirectUrl}`;

export const AuthenticationProvider = (props: OwnProps) => {
  const config = useContext(ConfigContext);
  const azureActiveDirectoryConfig = config.getConfig().azureActiveDirectory;

  const userManagerSettings = getUserManagerSettings({ azureActiveDirectoryConfig });

  const userManager = useRef<UserManager>(new UserManager(userManagerSettings));

  const [state, setState] = useState<State>({
    user: null,
    authenticationError: null,
    isLoggingIn: isLogInRedirectCallback(),
    isLoggingOut: false,
  });

  useOnMount(() => {
    if (isLogInRedirectCallback()) {
      handleLogInRedirectCallback();
    } else {
      userManager.current
        .getUser()
        .then(user => {
          if (user && !user.expired) {
            setState(currentState => ({ ...currentState, user }));
          } else {
            redirectToLogin();
          }
        })
        .catch(error => {
          // eslint-disable-next-line no-console
          console.error('Error getting user:', error);
          setState({
            user: null,
            authenticationError: error,
            isLoggingIn: false,
            isLoggingOut: false,
          });
        });
    }

    // This event is fired when the user logs out in another tab, or if their session has expired
    userManager.current.events.addUserSignedOut(handleUserLoggedOut);
  });

  const redirectToLogin = () => {
    sessionStorage.setItem(postLogInRedirectUrlStorageKey, window.location.pathname);
    setState(currentState => ({ ...currentState, isLoggingIn: true }));
    userManager.current.signinRedirect().catch(error => {
      // eslint-disable-next-line no-console
      console.error('Error handling redirecting to login:', error);
      setState({
        user: null,
        authenticationError: error,
        isLoggingIn: false,
        isLoggingOut: false,
      });
    });
  };

  const handleLogInRedirectCallback = () => {
    userManager.current
      .signinRedirectCallback()
      .then((user: User | null) => {
        setState({
          user,
          authenticationError: null,
          isLoggingIn: false,
          isLoggingOut: false,
        });
        redirectToOriginalPageAfterLogIn();
      })
      .catch(error => {
        // eslint-disable-next-line no-console
        console.error('Error handling sign-in redirect:', error);
        setState({
          user: null,
          authenticationError: error,
          isLoggingIn: false,
          isLoggingOut: false,
        });
        redirectToOriginalPageAfterLogIn();
      });
  };

  const redirectToOriginalPageAfterLogIn = () => {
    const originalUrl = sessionStorage.getItem(postLogInRedirectUrlStorageKey);
    sessionStorage.removeItem(postLogInRedirectUrlStorageKey);
    navigate(originalUrl || '/', { replace: true });
  };

  const logOut = () => {
    setState(currentState => ({ ...currentState, isLoggingOut: true }));
    userManager.current.signoutRedirect();
  };

  const handleUserLoggedOut = () => {
    navigate(loggedOutUrl);
  };

  const getAccessToken = async (): Promise<string> => {
    try {
      let user = await userManager.current.getUser();

      if (user == null || user.expired) {
        // eslint-disable-next-line no-console
        console.log('Access token expired, initiating silent renewal');
        user = await userManager.current.signinSilent();
      }

      return user.access_token;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error obtaining access token', error);
      redirectToLogin();
      throw error;
    }
  };

  const authenticationContextValue: AuthenticationContextValue = {
    getAccessToken,
    logOut,
  };

  return (
    <AuthenticationContext.Provider value={authenticationContextValue}>
      {props.children({
        ...state,
        isAuthenticated: state.user != null,
        redirectToLogin,
        logOut,
      })}
    </AuthenticationContext.Provider>
  );
};
